import { useMemo, useCallback, useState, useEffect, useRef, type ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import {
  makeStyles,
  tokens,
  Text,
  Accordion,
  AccordionItem,
  AccordionHeader,
  AccordionPanel,
  Spinner,
  Switch,
  Button,
  MessageBar,
  MessageBarActions,
  MessageBarBody,
} from '@fluentui/react-components';
import { SparkleRegular, ChevronRightRegular, ShieldKeyholeRegular } from '@fluentui/react-icons';
import { SearchableDropdownWithAddAll } from '@/components/SearchableDropdownWithAddAll';
import { useNodeEditorStore } from '@/stores/nodeEditorStore';
import type { ParameterValue } from '@/stores/nodeEditor/parameterUtils';
import { useAgentChatAguiStore } from '@/stores/agentChatAguiStore';
import { useConnectionStore } from '@/stores/connectionStore';
import { useWorkflowStore } from '@/stores/workflowStore';
import { useNotificationStore } from '@/stores/notificationStore';
import { useAuth } from '@/auth/useAuth';
import { StringField } from '../fields/StringField';
import { CopyableField } from '../fields/CopyableField';
import { TextAreaField } from '../fields/TextAreaField';
import { NumberField } from '../fields/NumberField';
import { BooleanField } from '../fields/BooleanField';
import { DropdownField } from '../fields/DropdownField';
import { ComboboxField } from '../fields/ComboboxField';
import { ArrayField } from '../fields/ArrayField';
import { DictionaryField } from '../fields/DictionaryField';
import { CodeField, CodeLanguage } from '../fields/CodeField';
import { ConditionField } from '../fields/ConditionField';
import { SchemaField } from '../fields/SchemaField';
import { TableField } from '../fields/TableField';
import { AuthenticationField } from '../fields/AuthenticationField';
import { DeploymentField } from './DeploymentField';
import { AgentInstructionField } from '../fields/AgentInstructionField';
import { InitializeVariableField } from '../fields/InitializeVariableField';
import { VariableNameField } from '../fields/VariableNameField';
import { SetVariableField } from '../fields/SetVariableField';
import { WorkflowPickerField } from '../fields/WorkflowPickerField';
import { ReadOnlyExternalField } from '../fields/ReadOnlyExternalField';
import { RecurrenceField } from '../fields/RecurrenceField';
import { HtmlField } from '../fields/HtmlField';
import { FilePickerField } from '../fields/FilePickerField';
import { FieldActionChips } from '../parameters/FieldActionChips';
import { FieldForkContainer } from '../parameters/FieldForkContainer';
import { TOKENIZABLE_PARAMETER_TYPES } from '../fields/tokenizableTypes';
import type { RecurrenceValue } from '@/types/recurrence';
import type { AgentParameterSchema } from '../fields/AgentParameterMenu';
import { isValueSegmentArray, segmentsToString, generateSegmentId, ValueSegmentType } from '@/types/valueSegment';
import type { ValueSegment } from '@/types/valueSegment';
import { useOperationManifestData } from '@/hooks/useOperationManifest';
import { useNodeOperationInfo } from '@/hooks/useNodeOperationInfo';
import { useDynamicSchema } from '@/hooks/useDynamicSchema';
import { useTriggerCallbackUrl } from '@/hooks/useTriggerCallbackUrl';
import { useConnectorSwagger } from '@/hooks/useConnectorSwagger';
import { useDynamicOptions, hasDynamicValuesConfig } from '@/hooks/useDynamicOptions';
import { getInputsDynamicSchema } from '@/services/operationManifestService';
import { getOnSelectMapping, applyOnSelectMapping } from '@/services/onSelectMapping';
import { ParameterVisibility, ParameterEditor } from '@/types/operationParameter';
import type { ParameterInfo, DynamicSchemaConfig } from '@/types/operationParameter';
import { usePreviewOutputs } from '@/hooks/useNodePreviewData';
import { useCopyWithToast } from '@/hooks/useCopyWithToast';
import { shouldShowParameter } from '@/utils/parameters';
import { extractPathParameters, mergeSwaggerAndDynamicSchemaParams, stripConnectionIdPrefix } from '@/stores/nodeEditor/swaggerParameterUtils';
import { isFoundryAgentAction, isCodingAgentHarnessType } from '@/types/workflowDefinition';
import type { Action } from '@/types/workflowDefinition';
import { readAgentHarnessType } from '../../workflowHelpers';
import { AgentToolsSection } from './AgentToolsSection';
import type { WorkflowActionInfo } from '@/components/WorkflowDesigner/CodeEditor/extensions/jsAutocomplete';
import type { DeploymentOption } from '@/hooks/useAzureResources';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  parameterGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  nestedGroupAccordion: {
    // Collapsible container that wraps children of a flattened nested-object
    // parameter (e.g. Service Bus `message`). Visually groups them under the
    // parent's display name without breaking the flat parameter layout.
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground2,
    '& .fui-AccordionHeader': {
      padding: 0,
    },
    '& .fui-AccordionHeader__button': {
      padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
    },
    '& .fui-AccordionPanel': {
      padding: `0 ${tokens.spacingHorizontalM} ${tokens.spacingVerticalM}`,
      margin: 0,
    },
  },
  nestedGroupHeader: {
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground2,
  },
  nestedGroupBody: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  noParams: {
    color: tokens.colorNeutralForeground3,
    fontStyle: 'italic',
    textAlign: 'center',
    padding: tokens.spacingVerticalL,
  },
  askOttoBanner: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorBrandStroke2}`,
    backgroundColor: tokens.colorBrandBackground2,
    marginBottom: tokens.spacingVerticalM,
    cursor: 'pointer',
    transition: `background-color ${tokens.durationFast} ${tokens.curveEasyEase}, border-color ${tokens.durationFast} ${tokens.curveEasyEase}`,
    width: '100%',
    textAlign: 'left',
    // Reset button defaults
    outline: 'none',
    fontFamily: 'inherit',
    '&:hover': {
      backgroundColor: tokens.colorBrandBackground2Hover,
      border: `1px solid ${tokens.colorBrandStroke1}`,
    },
    '&:focus-visible': {
      outline: `2px solid ${tokens.colorStrokeFocus2}`,
      outlineOffset: '1px',
    },
    '&:active': {
      backgroundColor: tokens.colorBrandBackground2Pressed,
    },
    '&:disabled': {
      cursor: 'not-allowed',
      opacity: 0.5,
    },
  },
  askOttoBannerText: {
    flex: 1,
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },
  askOttoBannerIcon: {
    color: tokens.colorBrandForeground1,
    flexShrink: 0,
    fontSize: tokens.fontSizeBase500,
  },
  askOttoBannerChevron: {
    color: tokens.colorNeutralForeground3,
    flexShrink: 0,
    fontSize: tokens.fontSizeBase400,
  },
  loading: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: tokens.spacingVerticalL,
    gap: tokens.spacingHorizontalS,
  },
  builtInToolsSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
    padding: tokens.spacingVerticalM,
    backgroundColor: tokens.colorNeutralBackground3,
    borderRadius: tokens.borderRadiusMedium,
  },
  builtInToolsHeader: {
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },
  builtInToolsToggle: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  builtInToolsLabel: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
  },
  builtInToolsDescription: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground3,
  },
  bodyFieldGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  dynamicSchemaError: {
    color: tokens.colorPaletteRedForeground1,
  },
  dynamicSchemaWaiting: {
    color: tokens.colorNeutralForeground3,
    fontStyle: 'italic',
  },
  // Compact-mode wrapper around each parameter row. The chips render in
  // each field's labelRow on the right edge (via the field's
  // `endActions` slot for fields that support it; absolutely positioned
  // top-right of the row otherwise — see `externalChipSlot`).
  //
  // Visibility model: the FieldActionChips wrapper (`data-otto-field-chips`)
  // is opacity:0 by default and only revealed when the row is hovered or
  // contains focus. This keeps the parameter list visually quiet while
  // still surfacing the affordance the moment the user expresses intent
  // (mouse over, click into the input, or tab to the chip itself —
  // `:focus-within` covers all three). We deliberately use opacity (not
  // `display: none` / `visibility: hidden`) so the chips remain in the
  // tab order and the row layout doesn't reflow when they appear.
  fieldRow: {
    // `position: relative` so externally-rendered chips can anchor to the
    // row's top-right corner via `externalChipSlot` (browse-mode fields
    // that don't expose an `endActions` slot — Combobox, Condition,
    // Authentication, Recurrence, etc. — render their chips here).
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
    '& [data-otto-field-chips]': {
      opacity: 0,
      transitionProperty: 'opacity',
      transitionDuration: tokens.durationFast,
      transitionTimingFunction: tokens.curveEasyEase,
      '@media (prefers-reduced-motion: reduce)': {
        transitionDuration: '0.01ms',
      },
    },
    '&:hover [data-otto-field-chips], &:focus-within [data-otto-field-chips]': {
      opacity: 1,
    },
  },
  // External chip slot for fields that don't accept an `endActions`
  // prop (composite editors, pickers, toggles). Anchors the chips at
  // the top-right of the row so they sit at roughly the same baseline
  // as the field's own label row. The chips themselves are still
  // hover/focus-revealed by the parent `fieldRow` rule above, so this
  // is purely positional.
  externalChipSlot: {
    position: 'absolute',
    top: 0,
    right: 0,
    // Small inset so the chips don't sit flush against the row edge —
    // matches the visual padding of the field components' own label
    // rows.
    paddingTop: tokens.spacingVerticalXXS,
    // Above the field's own label-row content so the chip's hit area
    // isn't blocked by an overlapping label.
    zIndex: 1,
  },
  manifestError: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
    padding: tokens.spacingVerticalM,
    backgroundColor: tokens.colorPaletteRedBackground1,
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorPaletteRedBorder1}`,
  },
  manifestErrorTitle: {
    color: tokens.colorPaletteRedForeground1,
    fontWeight: tokens.fontWeightSemibold,
  },
  manifestErrorMessage: {
    color: tokens.colorPaletteRedForeground1,
    fontSize: tokens.fontSizeBase200,
  },
});

// Parameter definition type - maps to UI field types
type ParameterFieldType =
  | 'string'      // Single-line text input
  | 'text'        // Multi-line textarea
  | 'code'        // Code editor
  | 'number'      // Number input
  | 'boolean'     // Toggle/checkbox
  | 'dropdown'    // Dropdown (strict selection only)
  | 'combobox'    // Combobox (dropdown + custom value entry with expression support)
  | 'filepicker'  // Tree-based file/folder picker
  | 'html'        // Rich text HTML editor
  | 'array'       // Array editor
  | 'dictionary'  // Key-value dictionary editor
  | 'condition'   // Condition builder
  | 'authentication' // Authentication editor
  | 'schema'      // JSON schema editor
  | 'table'       // Table editor
  | 'copyable'    // Read-only copyable field
  | 'deployment'  // Model deployment selector
  | 'agentinstruction' // Agent instruction editor (system + user messages)
  | 'initializevariable' // Initialize variable editor (for workflow variables)
  | 'setvariable' // SetVariable composite editor (name dropdown + type-aware value)
  | 'variablename' // Variable name picker (dropdown of defined variables)
  | 'workflowpicker' // Workflow picker (dropdown of callable workflows in the same Logic App)
  | 'readonlyexternal' // Read-only display backed by an external system (with optional deep-link)
  | 'recurrence'; // Recurrence schedule editor

interface ParameterDef {
  name: string;
  displayName: string;
  type: ParameterFieldType;
  description?: string;
  required?: boolean;
  placeholder?: string;
  /** Default value from swagger (pre-populates the field) */
  defaultValue?: unknown;
  options?: { label: string; value: string }[];
  advanced?: boolean;
  /** Editor-specific options from x-ms-editor-options */
  editorOptions?: Record<string, unknown>;
  /** Schema for array items (for array type) */
  itemSchema?: {
    type?: string | undefined;
    $ref?: string | undefined;
    properties?: Record<string, unknown> | undefined;
    required?: string[] | undefined;
  };
  /** Original ParameterInfo for advanced editor rendering */
  _parameterInfo?: ParameterInfo;
  /**
   * Parent group metadata for nested parameters that were flattened from a
   * nested object schema. Adjacent params with the same `key` are wrapped in
   * a collapsible container in the parameter list (label = parent display name).
   */
  _parentGroup?: { key: string; label: string };
}

/**
 * Convert ParameterInfo from manifest to ParameterDef for rendering
 */
function parameterInfoToParameterDef(param: ParameterInfo): ParameterDef {
  // Map editor type to field type
  let type: ParameterFieldType = 'string';

  // Special case: deploymentId with connection requirement should use DeploymentField
  const schemaExtensions = param.schema as Record<string, unknown> | undefined;
  const isDeploymentIdWithConnection =
    (param.parameterName === 'deploymentId' || param.parameterKey === 'deploymentId') &&
    schemaExtensions?.['x-ms-connection-required'] === true;

  // Promote `format: html` to the Html editor (used by Outlook/Teams body
  // params that don't carry an explicit `x-ms-editor: html`).
  const effectiveEditor =
    param.schema?.format === 'html' ? ParameterEditor.Html : param.editor;

  if (isDeploymentIdWithConnection) {
    type = 'deployment';
  } else {
    switch (effectiveEditor) {
      case ParameterEditor.String:
        // Check schema type for numbers - even with String editor, integers should use NumberField
        if (param.schema?.type === 'integer' || param.schema?.type === 'number') {
          type = 'number';
        } else {
          type = 'string';
        }
        break;
      case ParameterEditor.Boolean:
        type = 'boolean';
        break;
      case ParameterEditor.Combobox:
      case ParameterEditor.DynamicCombobox:
        // Both static and dynamic comboboxes use combobox type
        // Dynamic options are fetched via useDynamicOptions hook and passed to ComboboxField
        type = 'combobox';
        break;
      case ParameterEditor.FilePicker:
        // File picker uses the FilePicker field type
        type = 'filepicker';
        break;
      case ParameterEditor.Html:
        // HTML editor uses the rich text editor
        type = 'html';
        break;
      case ParameterEditor.TextArea:
        type = 'text';
        break;
      case ParameterEditor.Code:
        type = 'code';
        break;
      case ParameterEditor.Array:
        type = 'array';
        break;
      case ParameterEditor.Dictionary:
        type = 'dictionary';
        break;
      case ParameterEditor.Condition:
        type = 'condition';
        break;
      case ParameterEditor.Authentication:
        type = 'authentication';
        break;
      case ParameterEditor.Schema:
        type = 'schema';
        break;
      case ParameterEditor.Table:
        type = 'table';
        break;
      case ParameterEditor.CopyPaste:
        type = 'copyable';
        break;
      case ParameterEditor.DateTime:
        // DateTime uses string input with date-time format
        type = 'string';
        break;
      case ParameterEditor.AgentInstruction:
        type = 'agentinstruction';
        break;
      case ParameterEditor.InitializeVariable:
        type = 'initializevariable';
        break;
      case ParameterEditor.VariableName:
        type = 'variablename';
        break;
      case ParameterEditor.WorkflowPicker:
        type = 'workflowpicker';
        break;
      case ParameterEditor.ReadOnlyExternal:
        type = 'readonlyexternal';
        break;
      case ParameterEditor.Recurrence:
        type = 'recurrence';
        break;
      default:
        // Check schema type for numbers
        if (param.schema?.type === 'integer' || param.schema?.type === 'number') {
          type = 'number';
        } else if (param.schema?.type === 'boolean') {
          type = 'boolean';
        } else if (param.schema?.type === 'object') {
          type = 'dictionary';
        } else if (param.schema?.type === 'array') {
          type = 'array';
        } else {
          type = 'string';
        }
    }
  }

  const def: ParameterDef = {
    name: param.parameterKey,
    displayName: param.label,
    type,
    required: param.required,
    advanced: param.visibility === ParameterVisibility.Advanced,
  };

  // Recurrence values are stored under '_recurrence' key in parameterValues
  if (type === 'recurrence') {
    def.name = '_recurrence';
  }

  // Only add optional properties if they have values
  if (param.schema?.description) {
    def.description = param.schema.description;
  }
  if (param.placeholder) {
    def.placeholder = param.placeholder;
  }
  if (param.defaultValue !== undefined) {
    def.defaultValue = param.defaultValue;
  }

  // Pass through editor options
  if (param.editorOptions) {
    def.editorOptions = param.editorOptions;
  }

  // Add enum options for dropdowns
  // Check for editorOptions.options first (from x-ms-editor-options), then fall back to schema.enum
  const editorOpts = param.editorOptions as { options?: { value: string; displayName?: string }[] } | undefined;
  if (editorOpts?.options && editorOpts.options.length > 0) {
    def.options = editorOpts.options.map((opt) => ({
      label: opt.displayName ?? String(opt.value),
      value: String(opt.value),
    }));
  } else if (param.schema?.enum && param.schema.enum.length > 0) {
    def.options = param.schema.enum.map((v) => ({
      label: String(v),
      value: String(v),
    }));
  }

  // Add itemSchema for array types
  if (type === 'array' && param.schema?.items) {
    const items = param.schema.items as {
      type?: string;
      $ref?: string;
      properties?: Record<string, unknown>;
      required?: string[];
    };
    def.itemSchema = {
      type: items.type,
      $ref: items.$ref,
      properties: items.properties,
      required: items.required,
    };
  }

  // Pass through the original ParameterInfo for dynamic dropdown support
  def._parameterInfo = param;

  // Surface parent group info for nested-object flattening so the renderer can
  // wrap children of the same parent in a collapsible group container.
  if (param.info?.parentProperty) {
    def._parentGroup = {
      key: param.info.parentProperty.key,
      label: param.info.parentProperty.label,
    };
  }

  return def;
}

/**
 * Check if a parameter's conditional visibility dependencies are met
 * Uses shouldShowParameter from parameterVisibility.ts for x-ms-input-dependencies
 * Also supports legacy dependencies format (param.info.dependencies?.parameters)
 */
function isConditionalVisibilityMet(
  param: ParameterInfo,
  parameterValues: Record<string, unknown>
): boolean {
  // First check new x-ms-input-dependencies format using shouldShowParameter utility
  if (!shouldShowParameter(param, parameterValues)) {
    return false;
  }

  // Also check legacy dependencies format (param.info.dependencies?.parameters)
  if (param.conditionalVisibility && param.info.dependencies?.parameters) {
    for (const dep of param.info.dependencies.parameters) {
      const currentValue = parameterValues[dep.parameterReference];
      // If the current value is not in the required values, hide the parameter
      if (!dep.requiredValues?.includes(currentValue as string)) {
        return false;
      }
    }
  }

  return true;
}

/**
 * Filter parameters for compact view
 * Shows: important visibility OR required parameters
 * Hides: internal visibility (always), hideInUI (always), advanced (in collapsed section)
 * Also hides: parameters with conditional visibility dependencies not met
 */
function filterParametersForCompactView(
  params: ParameterInfo[],
  parameterValues: Record<string, unknown>
): ParameterInfo[] {
  return params.filter((p) => {
    // Never show hideInUI parameters
    if (p.hideInUI) {
      return false;
    }
    // Never show internal parameters
    if (p.visibility === ParameterVisibility.Internal) {
      return false;
    }
    // Check conditional visibility dependencies
    if (!isConditionalVisibilityMet(p, parameterValues)) {
      return false;
    }
    // Show important parameters
    if (p.visibility === ParameterVisibility.Important) {
      return true;
    }
    // Show required parameters
    if (p.required) {
      return true;
    }
    // Show advanced in the advanced section (they'll be separated later)
    if (p.visibility === ParameterVisibility.Advanced) {
      return true;
    }
    // Show default visibility parameters
    return p.visibility === ParameterVisibility.Default;
  });
}

/**
 * Generate parameters dynamically from action inputs (reflection-based)
 * Used for ApiConnection and other connector-based actions where manifest isn't available
 */
function generateParametersFromInputs(inputs: Record<string, unknown>): ParameterDef[] {
  const params: ParameterDef[] = [];
  const advancedKeys = ['authentication', 'headers', 'queries', 'retryPolicy'];
  // Internal fields that should not be shown as user-editable parameters.
  // - host/serviceProviderConfiguration: connection routing metadata
  // - fetch/subscribe: ApiConnectionNotification trigger structure
  // - method/path/pathTemplate: swagger operation metadata (HTTP verb, endpoint path)
  //   These are serialized at inputs root but are NOT user-editable; the swagger
  //   operation definition determines them, and user-facing path params are extracted separately.
  const internalFields = ['host', 'serviceProviderConfiguration', 'fetch', 'subscribe', 'method', 'path', 'pathTemplate'];

  for (const [key, value] of Object.entries(inputs)) {
    // Skip internal fields (connection config, service provider config, etc.)
    if (internalFields.includes(key)) continue;

    const isAdvanced = advancedKeys.includes(key);
    const displayName = key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1');

    // Handle 'body' specially - it's often an object with sub-fields
    if (key === 'body' && typeof value === 'object' && value !== null && !Array.isArray(value)) {
      // Flatten body fields as top-level parameters
      for (const [bodyKey, bodyValue] of Object.entries(value as Record<string, unknown>)) {
        const bodyDisplayName = bodyKey.charAt(0).toUpperCase() + bodyKey.slice(1).replace(/([A-Z])/g, ' $1');
        params.push({
          name: `body.${bodyKey}`,
          displayName: bodyDisplayName,
          type: inferFieldType(bodyValue),
          description: `Body field: ${bodyKey}`,
        });
      }
      continue;
    }

    params.push({
      name: key,
      displayName,
      type: inferFieldType(value),
      advanced: isAdvanced,
    });
  }

  return params;
}

/**
 * Infer parameter field type from value
 */
function inferFieldType(value: unknown): ParameterDef['type'] {
  if (typeof value === 'boolean') return 'boolean';
  if (typeof value === 'number') return 'number';
  if (typeof value === 'object') return 'text'; // JSON editor for objects
  // Default to text for multiline strings, string for single line
  if (typeof value === 'string' && (value.includes('\n') || value.length > 100)) return 'text';
  return 'string';
}

/**
 * DynamicComboboxField - Wrapper that fetches dynamic options and passes them to ComboboxField
 * This consolidates dynamic option fetching with the standard ComboboxField component.
 */
interface DynamicComboboxFieldProps {
  name: string;
  label: string;
  value: string | ValueSegment[];
  onChange: (value: string | ValueSegment[]) => void;
  parameterInfo: ParameterInfo;
  staticOptions?: { label: string; value: string }[];
  description?: string | undefined;
  placeholder?: string | undefined;
  required?: boolean | undefined;
  disabled?: boolean | undefined;
  error?: string | undefined;
  showAgentParameterButton?: boolean | undefined;
  agentParameterSchema?: AgentParameterSchema | undefined;
  actionOutputs?: Record<string, unknown> | undefined;
  hasRunData?: boolean | undefined;
  isStale?: boolean | undefined;
}

const DynamicComboboxField = ({
  name,
  label,
  value,
  onChange,
  parameterInfo,
  staticOptions = [],
  description,
  placeholder,
  required,
  disabled,
  error,
  showAgentParameterButton,
  agentParameterSchema,
  actionOutputs,
  hasRunData,
  isStale,
}: DynamicComboboxFieldProps) => {
  // Use the dynamic options hook
  const {
    options: dynamicOptions,
    isLoading,
    isAuthError,
    hasLoaded,
    dependenciesSatisfied,
    missingDependencies,
    missingConnection,
    refetch,
  } = useDynamicOptions({
    parameterInfo,
    staticOptions,
  });

  // Foundry-style interactive consent. The resolver flips `isAuthError` when
  // it couldn't acquire a token silently; the user has to click Authorize to
  // trigger an interactive popup. Reuses Foundry auth — this is the only auth
  // path that surfaces `isAuthError` today, so we hard-wire to it.
  const { grantFoundryAccess } = useAuth();
  const addNotification = useNotificationStore((s) => s.addNotification);
  const [isAuthorizing, setIsAuthorizing] = useState(false);
  const handleAuthorize = useCallback(async () => {
    setIsAuthorizing(true);
    try {
      const token = await grantFoundryAccess();
      if (token) {
        refetch();
      } else {
        addNotification({
          type: 'error',
          title: 'Authorization failed',
          message:
            "Couldn't acquire a Foundry access token. The popup may have been blocked or consent was denied. Please try again.",
        });
      }
    } finally {
      setIsAuthorizing(false);
    }
  }, [grantFoundryAccess, refetch, addNotification]);

  // x-ms-on-select: when the user picks an option, fan its `data` payload
  // into sibling parameters per the manifest mapping.
  const setParameter = useNodeEditorStore((s) => s.setParameter);
  const onSelectMapping = useMemo(
    () => getOnSelectMapping(parameterInfo.schema as Record<string, unknown> | undefined),
    [parameterInfo.schema]
  );
  const handleChange = useCallback(
    (next: string | ValueSegment[]) => {
      onChange(next);
      if (!onSelectMapping) return;
      const selectedValue = isValueSegmentArray(next) ? segmentsToString(next) : next;
      if (typeof selectedValue !== 'string' || selectedValue.length === 0) return;
      const matched = dynamicOptions.find((opt) => opt.value === selectedValue);
      if (!matched?.data) return;
      applyOnSelectMapping(onSelectMapping, matched.data, setParameter);
    },
    [onChange, onSelectMapping, dynamicOptions, setParameter]
  );

  // Build waiting-state message. Missing connection takes precedence over
  // missing dependencies — until a connection exists, dependency resolution
  // is meaningless and the user needs to configure the connection first.
  const dependencyMessage = missingConnection
    ? 'Configure a connection to load options.'
    : !dependenciesSatisfied && missingDependencies.length > 0
      ? `Select: ${missingDependencies.join(', ')}`
      : undefined;

  // `useDynamicOptions` already returns the authoritative option set: static
  // fallback before the first successful load, and the dynamic result (even when
  // empty) afterwards — so it's passed straight to ComboboxField. When that
  // settled result is empty, surface an explicit "no items" header in the
  // dropdown instead of only offering "Enter custom value". Gated on a settled
  // load so it isn't shown while a (re)fetch is in flight.
  const noOptionsMessage =
    hasLoaded && !isLoading && dynamicOptions.length === 0 ? 'No items' : undefined;

  return (
    <>
      <ComboboxField
        name={name}
        label={label}
        value={value}
        options={dynamicOptions}
        onChange={handleChange}
        description={description}
        placeholder={placeholder}
        required={required}
        disabled={disabled}
        error={error}
        showAgentParameterButton={showAgentParameterButton}
        agentParameterSchema={agentParameterSchema}
        actionOutputs={actionOutputs}
        hasRunData={hasRunData}
        isStale={isStale}
        isLoadingOptions={isLoading}
        // This field is a freeform combobox: when dynamic option loading fails
        // (e.g. a 403 from a models endpoint that isn't wired up yet), the user
        // can still type the model name manually. Surfacing a red error here is
        // just noise, so we drop the load error and keep only the refresh
        // affordance (and the Foundry authorize MessageBar below).
        loadError={undefined}
        onRefresh={refetch}
        dependencyMessage={dependencyMessage}
        noOptionsMessage={noOptionsMessage}
      />
      {/* MessageBar rendered after the field so the parent `.fieldRow`'s
          absolute `externalChipSlot` (top: 0; right: 0) anchors over the
          combobox label row rather than the warning. */}
      {isAuthError && (
        <MessageBar intent="warning">
          <MessageBarBody>Foundry access requires authorization.</MessageBarBody>
          <MessageBarActions>
            <Button
              appearance="transparent"
              size="small"
              icon={<ShieldKeyholeRegular />}
              onClick={handleAuthorize}
              disabled={isAuthorizing}
            >
              {isAuthorizing ? 'Authorizing…' : 'Authorize'}
            </Button>
          </MessageBarActions>
        </MessageBar>
      )}
    </>
  );
};

export interface ParameterListProps {
  /** Show the agent parameter button on fields - for tools within an agent */
  showAgentParameterButton?: boolean;
}

/**
 * Stable identifier the FieldFork slice uses to track the active param.
 * Manifest-driven params carry a ParameterInfo with a generated id; the
 * legacy fallback path (`generateParametersFromInputs`) does not, so we
 * fall back to the param key. Either way the id is unique within the panel
 * and stable across renders.
 */
const getCompactParamForkId = (param: ParameterDef): string =>
  param._parameterInfo?.id ?? param.name;

/**
 * Editor types whose parameter value is a flat {@link ValueSegment}[] we
 * can safely tail-append to. The FieldFork popover commits selected
 * tokens / expressions directly into the field's value via `onChange`.
 */
const INSERT_MODE_FIELD_TYPES: ReadonlySet<ParameterFieldType> = new Set([
  'string',
  'text',
  'number',
  'dictionary',
  'array',
]);

/**
 * Editor types that accept the chips inside their own labelRow via the
 * `endActions` prop. Insert-mode fields all qualify (they were the
 * original users of `endActions`); the listed browse-mode editors carry
 * a visual / JSON mode toggle on the right edge of their labelRow that
 * the chips need to sit *next to* rather than overlap. Anything outside
 * this set falls back to `externalChipSlot` (absolute top-right anchor).
 */
const NATIVE_END_ACTIONS_TYPES: ReadonlySet<ParameterFieldType> = new Set([
  // Insert-mode fields
  'string',
  'text',
  'number',
  'boolean',
  'dictionary',
  'array',
  // Browse-mode composite editors with a labelRow toggle
  'condition',
  'authentication',
  'schema',
  'table',
  'html',
  // Browse-mode picker — has a labelRow above the input + Browse row
  // that hosts the chips, so they don't overlap the Browse button.
  'filepicker',
]);

/**
 * Whether the FieldFork popover should mutate the parameter value
 * (`'insert'`), surface the value via clipboard + toast (`'browse'`), or
 * not render chips at all (`null`).
 *
 * Mirrors the expanded view's `TOKENIZABLE_PARAMETER_TYPES` gate on the
 * underlying ParameterInfo, then categorizes by editor:
 *   - `'insert'`: editors backed by a TokenizedInput (see
 *     {@link INSERT_MODE_FIELD_TYPES}).
 *   - `'browse'`: composite editors, pickers, toggles, and rich/code
 *     editors. Chips are still useful (the user wants to look up an
 *     expression to drop into an inner sub-field) but the parameter
 *     value is structured / enum / non-segment so we surface it via
 *     clipboard instead of corrupting it with a tail-appended segment.
 *   - `null`: `copyable` (already read-only with its own copy button)
 *     and the legacy fallback path that can't mount FieldForkContainer's
 *     upstream-data hooks.
 */
function getCompactParamChipMode(param: ParameterDef): 'insert' | 'browse' | null {
  if (!param._parameterInfo) return null;
  if (!TOKENIZABLE_PARAMETER_TYPES.has(param._parameterInfo.type ?? 'any')) {
    return null;
  }
  if (param.type === 'copyable') return null;
  if (INSERT_MODE_FIELD_TYPES.has(param.type)) return 'insert';
  return 'browse';
}

/**
 * ParameterList - Renders parameter fields based on node type
 *
 * Uses operation manifests from src/data/manifests for built-in operations.
 * For Azure managed connectors, fetches parameter definitions from swagger.
 * Falls back to dynamic generation from inputs when no manifest is available.
 */
export const ParameterList = ({ showAgentParameterButton = false }: ParameterListProps) => {
  const styles = useStyles();
  const { node, parameterValues, setParameter, validationErrors, setSwaggerMetadata } = useNodeEditorStore();
  const isReadOnly = useNodeEditorStore((s) => s.isReadOnly);
  const openFieldFork = useNodeEditorStore((s) => s.openFieldFork);
  const fieldFork = useNodeEditorStore((s) => s.fieldFork);
  // Picker display map (`metadata: { [value]: display }`) so FilePickerField
  // shows the friendly path the user originally picked.
  const pickerDisplayValues = useNodeEditorStore((s) => s.pickerDisplayValues);
  const setPickerDisplayValue = useNodeEditorStore((s) => s.setPickerDisplayValue);
  const queuePrompt = useAgentChatAguiStore((s) => s.queuePrompt);
  const isStreaming = useAgentChatAguiStore((s) => s.isStreaming);
  const pendingPrompt = useAgentChatAguiStore((s) => s.pendingPrompt);
  const isAiBusy = isStreaming || pendingPrompt !== null;
  const isScopedEditEnabled = useFeatureFlag('scopedEdit');
  const copyWithToast = useCopyWithToast();

  // Get the agent parameter schema — prefer draft from parameterValues (live edits from
  // Tool tab), fall back to persisted node data. This keeps the Inputs tab in sync with
  // the Tool tab without requiring a panel close/reopen.
  const agentParameterSchema = useMemo((): AgentParameterSchema | undefined => {
    const draft = parameterValues['_agentParameterSchema'] as AgentParameterSchema | null | undefined;
    if (draft !== undefined) return draft ?? undefined;
    if (node?.toolData?.agentParameterSchema) {
      return node.toolData.agentParameterSchema as AgentParameterSchema;
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only recompute when the specific key changes
  }, [parameterValues['_agentParameterSchema'], node?.toolData?.agentParameterSchema]);

  // Get preview output data from last successful run
  const { outputs: actionOutputs, variables: variableOutputs, hasRunData, isStale } = usePreviewOutputs(node?.id ?? null);

  // Combine action outputs with variable values for expression evaluation
  // Variables need to be accessible as context.variables, but we also merge them into actionOutputs
  // for backwards compatibility with expressions that might reference them as action outputs
  const combinedActionOutputs = useMemo(() => {
    return {
      ...actionOutputs,
      // Add a special key for variables so TokenizedInput can access them
      __variables__: variableOutputs,
    };
  }, [actionOutputs, variableOutputs]);

  const nodeType = node?.nodeData?.type || '';
  const nodeInputs = (node?.nodeData as { inputs?: Record<string, unknown> })?.inputs;

  // Current workflow name (for excluding self in the WorkflowPicker editor).
  const { workflowName: currentWorkflowName } = useParams<{ workflowName: string }>();

  // Subscribe to workflow definition so workflowActions stays fresh when
  // actions/triggers are added, renamed, deleted, or the workflow is switched.
  const workflowDefinition = useWorkflowStore((s) => s.workflow?.definition);

  // Build workflow action info for JavaScript IntelliSense
  // Extracts action/trigger names from the workflow definition for workflowContext completions
  const workflowActions = useMemo((): WorkflowActionInfo[] => {
    if (!workflowDefinition) return [];

    const result: WorkflowActionInfo[] = [];

    // Add triggers
    if (workflowDefinition.triggers) {
      for (const triggerName of Object.keys(workflowDefinition.triggers)) {
        result.push({ name: triggerName, displayName: triggerName, isTrigger: true });
      }
    }

    // Add all actions
    if (workflowDefinition.actions) {
      for (const actionName of Object.keys(workflowDefinition.actions)) {
        result.push({ name: actionName, displayName: actionName });
      }
    }

    return result;
  }, [workflowDefinition]);
  
  // Check if this is an Agent node - needed early for conditional rendering
  const isAgentNode = nodeType === 'Agent';
  // Foundry agents share the 'Agent' node type but have their own manifest and
  // surface built-in tools via `agentModelSettings`, so the hardcoded native
  // Code Interpreter switch below must be hidden for them.
  const isFoundryAgentNode = useMemo(
    () => isAgentNode && !!node?.nodeData && isFoundryAgentAction(node.nodeData as Action),
    [isAgentNode, node?.nodeData],
  );

  // Use the centralized hook for operation info resolution
  // This handles all the complex logic of extracting operationId, apiId, path/method fallbacks
  const { operationInfo } = useNodeOperationInfo({
    nodeData: node?.nodeData,
    parameterValues,
  });

  // For built-in HTTP Request triggers, fetch the runtime callback URL via the
  // data-plane `listCallbackUrl` endpoint and surface it in the read-only
  // `callbackUrl` parameter (declared with `'x-ms-editor': 'copyable'` and
  // `'x-ms-serialization': { skip: true }` in the request manifest).
  //
  // The fetched value is rendered directly into the copyable field below;
  // we intentionally do NOT mirror it into parameterValues via setParameter,
  // because that would mark the editor dirty and trigger an auto-save every
  // time the panel opens for an untouched Request trigger.
  const isRequestTrigger = nodeType === 'Request';
  const triggerName = isRequestTrigger ? node?.name : undefined;
  const { data: triggerCallback, isFetching: isFetchingCallback } =
    useTriggerCallbackUrl(triggerName, isRequestTrigger);
  const triggerCallbackUrl = triggerCallback?.value;

  // Extract connection reference name for Agent nodes (for DeploymentField)
  // First check in-memory parameterValues (for newly selected connections),
  // then fall back to persisted nodeInputs.modelConfigurations
  const connectionReferenceName = useMemo((): string | undefined => {
    if (nodeType !== 'Agent') return undefined;

    // First check parameterValues for in-memory selection (e.g., _modelConnection_model1)
    // This captures connections selected in the panel but not yet saved
    const inMemoryConnection = parameterValues['_modelConnection_model1'] as string | undefined;
    if (inMemoryConnection) {
      return inMemoryConnection;
    }

    // Fall back to persisted data in nodeInputs
    if (!nodeInputs) return undefined;
    const modelConfigurations = (nodeInputs as { modelConfigurations?: Record<string, { referenceName?: string }> }).modelConfigurations;
    if (!modelConfigurations) return undefined;
    // Get the first model configuration's reference name
    const firstConfig = Object.values(modelConfigurations)[0];
    return firstConfig?.referenceName;
  }, [nodeType, parameterValues, nodeInputs]);

  // Whether this Agent action uses a coding-agent harness type. Passed to
  // DeploymentField to disable the GHCP `/models` fetch for coding agents
  // (the runtime route isn't plumbed yet). This only matters when the coding
  // agent happens to use a GHCP connection; API-key coding agents are unaffected.
  const isCodingAgent = useMemo((): boolean => {
    if (nodeType !== 'Agent') return false;
    const harnessType = readAgentHarnessType(
      parameterValues,
      nodeInputs as Record<string, unknown> | undefined,
    );
    return isCodingAgentHarnessType(harnessType);
  }, [nodeType, parameterValues, nodeInputs]);

  // Extract fetch path for notification triggers (used to resolve operationId from swagger)
  // Following Logic Apps UX pattern: operationId is not stored in workflow definition,
  // it's resolved dynamically from the swagger using the path
  const fetchPath = useMemo(() => {
    if (nodeType !== 'ApiConnectionNotification' || !nodeInputs) return undefined;
    const fetch = (nodeInputs as { fetch?: { pathTemplate?: { template?: string }; method?: string } }).fetch;
    return fetch?.pathTemplate?.template;
  }, [nodeType, nodeInputs]);

  const fetchMethod = useMemo(() => {
    if (nodeType !== 'ApiConnectionNotification' || !nodeInputs) return undefined;
    const fetch = (nodeInputs as { fetch?: { method?: string } }).fetch;
    return fetch?.method;
  }, [nodeType, nodeInputs]);


  // Fetch manifest parameters and swagger parser
  // Pass nodeType and fetchPath for notification trigger handling
  const { manifest, parameters: manifestParams, isLoading: manifestLoading, isFetching: manifestFetching, error: manifestError, swaggerParser: manifestSwaggerParser } = useOperationManifestData(operationInfo, { nodeType, fetchPath, fetchMethod });

  // =============================================================================
  // DYNAMIC SCHEMA INTEGRATION
  // Fetches additional parameters when a parameter has x-ms-dynamic-schema config
  // e.g., Teams connector generates Team/Channel/Message fields based on "Post in" selection
  // =============================================================================

  // Get connection context for dynamic schema fetching
  const nodeId = node?.id ?? '';
  const connectionMapping = useConnectionStore((state) => state.mapping);
  const connectionReferences = useConnectionStore((state) => state.references);

  // Get connection reference - try mapping first, then fallback to _connection
  const dynamicSchemaConnectionKey = useMemo(() => {
    let key = connectionMapping[nodeId];
    if (!key) {
      const fallbackKey = parameterValues['_connection'] as string | undefined;
      if (fallbackKey && connectionReferences[fallbackKey]) {
        key = fallbackKey;
      }
    }
    return key;
  }, [connectionMapping, nodeId, parameterValues, connectionReferences]);

  const dynamicSchemaConnectionReference = useMemo(() => dynamicSchemaConnectionKey
    ? connectionReferences[dynamicSchemaConnectionKey]
    : undefined, [dynamicSchemaConnectionKey, connectionReferences]);

  // Get connector ID from connection reference or parameterValues
  const dynamicSchemaConnectorId = useMemo(() => {
    const idFromData = dynamicSchemaConnectionReference?.api?.id ?? parameterValues['_apiId'] as string ?? '';
    // TODO: Remove this once new connections integration is completed.
    const formedId = idFromData.toLowerCase().includes('/managedapis') ? `/managedapis/${idFromData.split('/').slice(-1)[0]}` : idFromData;

    return formedId;
  }, [dynamicSchemaConnectionReference, parameterValues]);

  // Fetch swagger for the connector (required for legacy x-ms-dynamic-schema)
  const { data: connectorSwaggerResult } = useConnectorSwagger(dynamicSchemaConnectorId || null);
  const connectorSwaggerParser = useMemo(() => connectorSwaggerResult?.parser ?? null, [connectorSwaggerResult]);

  // Get auth token for dynamic schema API calls
  const { getArmToken } = useAuth();
  const [dynamicSchemaAccessToken, setDynamicSchemaAccessToken] = useState<string | null>(null);

  // Find the first parameter with dynamic schema configuration
  const dynamicSchemaParam = useMemo((): { param: ParameterInfo; config: DynamicSchemaConfig } | null => {
    if (!manifestParams) return null;

    for (const param of manifestParams) {
      if (param.info?.dynamicSchema?.operationId) {
        return { param, config: param.info.dynamicSchema };
      }
    }
    return null;
  }, [manifestParams]);

  // Fetch token when we have a dynamic schema parameter
  useEffect(() => {
    if (dynamicSchemaParam && !dynamicSchemaAccessToken) {
      getArmToken().then(setDynamicSchemaAccessToken);
    }
  }, [dynamicSchemaParam, dynamicSchemaAccessToken, getArmToken]);

  // Convert parameter values to simple strings for dependency resolution
  // Also returns which params were extracted via swagger (for syncing to store)
  const { simpleParameterValues, swaggerExtractedPathParams } = useMemo((): {
    simpleParameterValues: Record<string, unknown>;
    swaggerExtractedPathParams: string[];
  } => {
    const result: Record<string, unknown> = {};
    const extractedParamNames: string[] = [];

    for (const [key, val] of Object.entries(parameterValues)) {
      if (isValueSegmentArray(val)) {
        result[key] = segmentsToString(val);
      } else {
        result[key] = val;
      }
    }

    // Flatten body object if it exists (for swagger operations where body is stored as object)
    // This ensures individual body properties like body.content, content are available for lookup
    const bodyValue = result['body'];
    if (bodyValue && typeof bodyValue === 'object' && !Array.isArray(bodyValue)) {
      const flattenBody = (obj: Record<string, unknown>, bodyPrefix: string, noPrefix: string) => {
        for (const [key, value] of Object.entries(obj)) {
          const bodyPrefixedKey = bodyPrefix ? `${bodyPrefix}.${key}` : `body.${key}`;
          const noPrefixKey = noPrefix ? `${noPrefix}.${key}` : key;

          if (value === null || value === undefined) {
            continue;
          } else if (typeof value === 'object' && !Array.isArray(value)) {
            // Nested object - store the object AND recursively flatten
            result[bodyPrefixedKey] = value;
            result[noPrefixKey] = value;
            flattenBody(value as Record<string, unknown>, bodyPrefixedKey, noPrefixKey);
          } else {
            // Primitive or array - store as-is with both key formats
            result[bodyPrefixedKey] = value;
            result[noPrefixKey] = value;
          }
        }
      };
      flattenBody(bodyValue as Record<string, unknown>, '', '');
    }

    // Re-extract path-param values from the saved `_path` for swagger ops.
    // Triggers whenever the path carries an unresolved `{name}` placeholder
    // OR a dynamic `${...}` / `@{...}` expression — the serializer keeps raw
    // token expressions verbatim instead of wrapping them in
    // `encodeURIComponent`, so without this they'd never get re-mapped to
    // their canonical parameter names on reload. `extractPathParameters`
    // already decodes literal-string wrappers and preserves expressions
    // verbatim, so values are stored as-is.
    const path =
      typeof result['_path'] === 'string' ? (result['_path'] as string) : undefined;
    const methodValue = result['_method'] as string | undefined;
    const pathNeedsExtraction =
      !!path && (path.includes('${') || path.includes('@{') || /\{[a-zA-Z_]\w*\}/.test(path));

    if (path && pathNeedsExtraction) {
      let extracted: Record<string, string> = {};

      // Use swagger parser from manifest (preferred) or dynamic schema to find operation and get path template
      // This enables proper path parameter extraction using swagger metadata
      const swaggerParser = manifestSwaggerParser ?? connectorSwaggerParser;

      if (swaggerParser && methodValue) {
        const operation = swaggerParser.getOperationByPathAndMethod(path, methodValue);

        if (operation) {
          const swaggerPathTemplate = stripConnectionIdPrefix(operation.path);

          extracted = extractPathParameters(path, swaggerPathTemplate);
        }
      }

      // Fallback: Check if swaggerPathTemplate is stored in parameterValues (from node metadata)
      if (Object.keys(extracted).length === 0) {
        const storedTemplate = result['_swaggerPathTemplate'] as string | undefined;
        if (storedTemplate) {
          extracted = extractPathParameters(path, storedTemplate);
        }
      }

      for (const [paramName, rawValue] of Object.entries(extracted)) {
        if (!(paramName in result)) {
          result[paramName] = rawValue;
          extractedParamNames.push(paramName);
        }
      }
    }

    return { simpleParameterValues: result, swaggerExtractedPathParams: extractedParamNames };
  }, [parameterValues, manifestSwaggerParser, connectorSwaggerParser]);

  // Debounce simpleParameterValues for dynamic schema fetching (wait 250ms after user stops typing)
  const [debouncedParameterValues, setDebouncedParameterValues] = useState<Record<string, unknown>>(simpleParameterValues);
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedParameterValues(simpleParameterValues);
    }, 250);
    return () => clearTimeout(timer);
  }, [simpleParameterValues]);

  // Sync swagger-extracted path parameters to the store so dynamic option fetching can access them
  // This is needed because useDynamicOptions reads from useNodeEditorStore.parameterValues for dependency resolution
  useEffect(() => {
    // Only sync params that were actually extracted via swagger (tracked in swaggerExtractedPathParams)
    for (const paramName of swaggerExtractedPathParams) {
      if (paramName in simpleParameterValues && !(paramName in parameterValues)) {
        const value = simpleParameterValues[paramName];
        if (value !== undefined && value !== null && value !== '') {
          setParameter(paramName, value as string);
        }
      }
    }
  }, [swaggerExtractedPathParams, simpleParameterValues, parameterValues, setParameter]);

  // Store swagger metadata for ApiConnection serialization
  // This enables proper serialization with method, path (with substitution), queries, etc.
  useEffect(() => {
    // Only for ApiConnection-family nodes with manifest params loaded
    const isSwaggerNode = nodeType === 'ApiConnection' || nodeType === 'ApiConnectionWebhook';
    if (!isSwaggerNode || !manifestParams || manifestParams.length === 0) {
      return;
    }

    // Get the swagger operation to extract method and path template
    const swaggerParser = manifestSwaggerParser;
    if (!swaggerParser || !operationInfo?.operationId) {
      return;
    }

    // Look up the operation in swagger
    const operation = swaggerParser.getOperationByOperationId(operationInfo.operationId);
    if (!operation) {
      return;
    }

    const pathTemplate = stripConnectionIdPrefix(operation.path || '');

    // Get method from operation
    const method = operation.method || 'get';

    // Get encoding level from parameters (default to double encoding)
    // Default to single encoding (1) for path parameters
    // Parameter-specific encoding from x-ms-url-encoding takes precedence
    const pathEncoding = 1;

    // Determine connection format based on node type
    const connectionFormat =
      nodeType === 'ApiConnectionWebhook'
        ? ('ApiConnectionWebhook' as const)
        : ('ApiConnection' as const);

    // Get parameters directly from swagger parser - these have correct info.in values
    // (manifestParams from the manifest schema loses info.in during conversion)
    const swaggerParams = swaggerParser.getInputParameters(operationInfo.operationId, {
      excludeInternalParameters: false, // Need ALL params including path params for serialization
    });

    // Store swagger params (with info.in) for use during serialization
    setSwaggerMetadata(swaggerParams, {
      method,
      pathTemplate,
      pathEncoding,
      connectionFormat,
      connectorId: operationInfo.connectorId,
      operationId: operationInfo.operationId,
    });
  }, [nodeType, manifestParams, manifestSwaggerParser, operationInfo?.operationId, operationInfo?.connectorId, setSwaggerMetadata]);

  // Set swagger metadata for connector-based operations (when no local manifest exists)
  // This handles API connectors like Azure Blob where swagger is fetched from the connector API
  useEffect(() => {
    // Only for ApiConnection-family nodes
    const isSwaggerNode = nodeType === 'ApiConnection' || nodeType === 'ApiConnectionWebhook';
    if (!isSwaggerNode) {
      return;
    }

    // Only run if we have connector swagger but NO manifest swagger
    // (manifest swagger is handled by the previous useEffect)
    if (manifestSwaggerParser || !connectorSwaggerParser || !operationInfo?.operationId) {
      return;
    }

    // Look up the operation in swagger
    const operation = connectorSwaggerParser.getOperationByOperationId(operationInfo.operationId);
    if (!operation) {
      return;
    }

    const pathTemplate = stripConnectionIdPrefix(operation.path || '');

    // Get method from operation
    const method = operation.method || 'get';

    // Default to single encoding (1) for path parameters
    const pathEncoding = 1;

    // Determine connection format based on node type
    const connectionFormat =
      nodeType === 'ApiConnectionWebhook'
        ? ('ApiConnectionWebhook' as const)
        : ('ApiConnection' as const);

    // Get parameters directly from swagger parser - these have correct info.in values
    const swaggerParams = connectorSwaggerParser.getInputParameters(operationInfo.operationId, {
      excludeInternalParameters: false, // Need ALL params including path params for serialization
    });

    // Store swagger params (with info.in) for use during serialization
    setSwaggerMetadata(swaggerParams, {
      method,
      pathTemplate,
      pathEncoding,
      connectionFormat,
      connectorId: operationInfo.connectorId,
      operationId: operationInfo.operationId,
    });
  }, [nodeType, manifestSwaggerParser, connectorSwaggerParser, operationInfo?.operationId, operationInfo?.connectorId, setSwaggerMetadata]);


  // Use the dynamic schema hook to fetch additional parameters
  const {
    dynamicParameters: dynamicSchemaParameters,
    isLoading: dynamicSchemaLoading,
    // error and dependenciesSatisfied are used for conditional UI rendering
    error: dynamicSchemaError,
    dependenciesSatisfied: dynamicSchemaDependenciesSatisfied,
    missingConnection: dynamicSchemaMissingConnection,
  } = useDynamicSchema({
    schemaConfig: dynamicSchemaParam?.config,
    connectionReference: dynamicSchemaConnectionReference,
    connectorId: dynamicSchemaConnectorId,
    parameterValues: debouncedParameterValues,
    parentParameterName: dynamicSchemaParam?.param.parameterName ?? '',
    bodyKeyPrefix: dynamicSchemaParam?.param.info?.bodyKeyPath,
    nodeId,
    autoFetch: true,
    swagger: connectorSwaggerParser,
  });

  // Root-level x-ms-dynamic-properties on the inputs schema — used by runtime
  // literal-type built-ins (ChunkText, etc.) where the dynamic call augments
  // the static inputs object rather than expanding one of its properties.
  // Results append to (not replace) the static parameter list.
  const inputsDynamicSchemaConfig = useMemo(
    () => (manifest ? getInputsDynamicSchema(manifest) : undefined),
    [manifest],
  );

  const {
    dynamicParameters: inputsDynamicParameters,
  } = useDynamicSchema({
    schemaConfig: inputsDynamicSchemaConfig,
    connectionReference: dynamicSchemaConnectionReference,
    connectorId: operationInfo?.connectorId ?? '',
    parameterValues: debouncedParameterValues,
    parentParameterName: '__inputs__',
    nodeId,
    autoFetch: true,
    swagger: null,
  });

  // When dynamic schema parameters are loaded, merge them with swagger path params for serialization
  // - Path params come from swagger (have info.in='path' and encode metadata)
  // - Body params come from dynamic schema (have info.in='body')
  // The merged list ensures proper routing during serialization
  useEffect(() => {
    // Only for ApiConnection-family nodes with dynamic params loaded
    const isSwaggerNode = nodeType === 'ApiConnection' || nodeType === 'ApiConnectionWebhook';
    if (!isSwaggerNode || !dynamicSchemaParameters || dynamicSchemaParameters.length === 0) {
      return;
    }

    // Get method, path template, and PATH parameters from swagger
    let swaggerMethod = '';
    let swaggerPathTemplate = '';
    let swaggerParamsToMerge: ParameterInfo[] = [];
    const swaggerParser = manifestSwaggerParser ?? connectorSwaggerParser;
    if (swaggerParser && operationInfo?.operationId) {
      const operation = swaggerParser.getOperationByOperationId(operationInfo.operationId);
      if (operation?.method) {
        swaggerMethod = operation.method;
      }
      if (operation?.path) {
        swaggerPathTemplate = stripConnectionIdPrefix(operation.path);
      }

      const allSwaggerParams = swaggerParser.getInputParameters(operationInfo.operationId, {
        excludeInternalParameters: false,
      });
      swaggerParamsToMerge = allSwaggerParams;
    }

    // Merge: filtered swagger params + dynamic schema body params, dropping any
    // swagger placeholder that the dynamic schema expanded. See
    // `mergeSwaggerAndDynamicSchemaParams` for the filter rationale.
    const mergedParams = mergeSwaggerAndDynamicSchemaParams(
      swaggerParamsToMerge,
      dynamicSchemaParameters,
    );

    // Pass merged params for body/path routing based on their info.in values
    setSwaggerMetadata(mergedParams, {
      method: swaggerMethod,
      pathTemplate: swaggerPathTemplate,
      pathEncoding: 1, // Single encoding is the default for most operations
      connectionFormat:
        nodeType === 'ApiConnectionWebhook'
          ? ('ApiConnectionWebhook' as const)
          : ('ApiConnection' as const),
      connectorId: operationInfo?.connectorId ?? '',
      operationId: operationInfo?.operationId ?? '',
    });
  }, [nodeType, dynamicSchemaParameters, operationInfo?.operationId, operationInfo?.connectorId, setSwaggerMetadata, manifestSwaggerParser, connectorSwaggerParser]);

  // Convert dynamic schema parameters to ParameterDef for rendering
  const dynamicSchemaParameterDefs = useMemo(() => {
    if (!dynamicSchemaParameters || dynamicSchemaParameters.length === 0) return [];
    return dynamicSchemaParameters.map(parameterInfoToParameterDef);
  }, [dynamicSchemaParameters]);

  // Convert inputs-root dynamic schema parameters (e.g. ChunkText/TokenSize
  // additions like EncodingModel, maxPageLength) to ParameterDef. Kept
  // separate from `dynamicSchemaParameterDefs` because these append rather
  // than replace the dependency parameter.
  const inputsDynamicSchemaParameterDefs = useMemo(() => {
    if (!inputsDynamicParameters || inputsDynamicParameters.length === 0) return [];
    return inputsDynamicParameters.map(parameterInfoToParameterDef);
  }, [inputsDynamicParameters]);

  // Filter manifest parameters for compact view and convert to ParameterDef.
  // SetVariable collapses its `name` + `value` manifest params into a single
  // composite ParameterDef so we can render the variable-editor-style
  // dropdown + type-aware value control as one unit.
  const manifestParameterDefs = useMemo(() => {
    if (!manifestParams || manifestParams.length === 0) return [];

    if (nodeType === 'SetVariable') {
      const nameParam = manifestParams.find((p) => p.parameterName === 'name');
      const valueParam = manifestParams.find((p) => p.parameterName === 'value');
      if (nameParam && valueParam) {
        const composite: ParameterDef = {
          name: 'name',
          displayName: 'Variable',
          type: 'setvariable',
          required: nameParam.required || valueParam.required,
          ...(nameParam.schema?.description ? { description: nameParam.schema.description } : {}),
          _parameterInfo: nameParam,
        };
        const others = manifestParams.filter(
          (p) => p.parameterName !== 'name' && p.parameterName !== 'value',
        );
        const filteredOthers = filterParametersForCompactView(others, simpleParameterValues);
        return [composite, ...filteredOthers.map(parameterInfoToParameterDef)];
      }
    }

    const filtered = filterParametersForCompactView(manifestParams, simpleParameterValues);
    return filtered.map(parameterInfoToParameterDef);
  }, [manifestParams, simpleParameterValues, nodeType]);

  // Merge manifest parameters with dynamic schema parameters
  // When dynamic schema params exist, HIDE the parent parameter and replace with generated params
  const mergedParameterDefs = useMemo(() => {
    let result = manifestParameterDefs;

    if (dynamicSchemaParameterDefs.length > 0) {
      // Find the trigger parameter that has dynamicSchema config
      const triggerParamIndex = manifestParameterDefs.findIndex(
        (p) => p._parameterInfo?.info?.dynamicSchema?.operationId
      );

      if (triggerParamIndex === -1) {
        // No trigger found, append dynamic params at the end
        result = [...manifestParameterDefs, ...dynamicSchemaParameterDefs];
      } else {
        // HIDE the parent parameter (the one with dynamicSchema) and replace with generated params
        // This follows LogicAppsUX pattern where the body param is hidden once schema is loaded
        const before = manifestParameterDefs.slice(0, triggerParamIndex);
        const after = manifestParameterDefs.slice(triggerParamIndex + 1);
        result = [...before, ...dynamicSchemaParameterDefs, ...after];
      }
    }

    // Inputs-root dynamic schema always appends — the static inputs (e.g.
    // ChunkText's `chunkingStrategy`, `text`) remain visible alongside the
    // dynamically-injected siblings.
    if (inputsDynamicSchemaParameterDefs.length > 0) {
      result = [...result, ...inputsDynamicSchemaParameterDefs];
    }

    return result;
  }, [manifestParameterDefs, dynamicSchemaParameterDefs, inputsDynamicSchemaParameterDefs]);

  // Determine if we're still waiting for manifest to load
  // This is true when we have operationInfo but manifest hasn't been fetched yet
  // For notification triggers, parameters come directly from swagger (manifestParams), not from manifest
  // So we're not waiting if we already have parameters
  const isWaitingForManifest = useMemo(() => operationInfo && !manifest && !manifestError && manifestParams.length === 0 && !manifestLoading && !manifestFetching, [operationInfo, manifest, manifestError, manifestParams, manifestLoading, manifestFetching]);

  // Use merged parameters (manifest + dynamic schema), or fall back to dynamic generation from inputs
  // Only fall back if we're not waiting for a manifest to load
  const parameters = useMemo(() => {
    // Use merged parameters if available (includes manifest + dynamic schema params)
    if (mergedParameterDefs.length > 0) {
      return mergedParameterDefs;
    }
    // Don't fall back to generated params if we're still waiting for manifest
    if (isWaitingForManifest) {
      return [];
    }
    // For connector-based actions (ApiConnection, etc.), generate from actual inputs
    // Fall back if manifest loaded with no params OR if no manifest available
    if (nodeInputs && typeof nodeInputs === 'object') {
      return generateParametersFromInputs(nodeInputs);
    }
    return [];
  }, [mergedParameterDefs, nodeInputs, isWaitingForManifest]);

  const requiredParams = parameters.filter(p => !p.advanced);
  const advancedParams = parameters.filter(p => p.advanced);

  // Advanced params the user explicitly added via the picker; keeps them
  // visible inline before they have any stored value.
  const [manuallyShownAdvanced, setManuallyShownAdvanced] = useState<ReadonlySet<string>>(
    () => new Set<string>(),
  );

  const setParameters = useNodeEditorStore((s) => s.setParameters);

  // Seed parameterValues with manifest defaults whenever nothing is stored,
  // including optional/advanced params. Without this, array/object defaults
  // render only as placeholder text and never serialize, and the advanced
  // picker already treats `defaultValue !== undefined` as inline+locked.
  // Batched via setParameters so opening a node schedules a single autosave
  // rather than one per default. Ref avoids stale-closure re-runs while
  // reading the latest values.
  const parameterValuesRef = useRef(parameterValues);
  parameterValuesRef.current = parameterValues;

  useEffect(() => {
    const seed: Record<string, ParameterValue> = {};
    for (const param of parameters) {
      if (param.defaultValue === undefined) continue;
      const current = parameterValuesRef.current[param.name];
      if (current === undefined || current === null) {
        seed[param.name] = param.defaultValue as ParameterValue;
      }
    }
    if (Object.keys(seed).length > 0) {
      setParameters(seed);
    }
  }, [parameters, setParameters]);

  // Check if all required params are empty (unfilled by user)
  const isParamEmpty = useCallback((p: { name: string }) => {
    const val = parameterValues[p.name];
    if (val === undefined || val === null || val === '') return true;
    if (isValueSegmentArray(val)) return segmentsToString(val as ValueSegment[]).trim() === '';
    return false;
  }, [parameterValues]);

  const allParamsEmpty = requiredParams.length > 0 && requiredParams.every(isParamEmpty);
  const allParamsFilled = requiredParams.length > 0 && requiredParams.every(p => !isParamEmpty(p));
  const someParamsConfigured = requiredParams.length > 0 && !allParamsEmpty && !allParamsFilled;

  // Advanced params with a manifest default are always inline (they serialize
  // even if the user never edits them), so their picker option is locked.
  const advancedPicker = useMemo(() => {
    const inlineParams = advancedParams.filter(
      (p) => !isParamEmpty(p) || p.defaultValue !== undefined || manuallyShownAdvanced.has(p.name),
    );
    const inlineKeys = inlineParams.map((p) => p.name);
    const options = advancedParams.map((p) => ({
      value: p.name,
      label: p.displayName,
      disabled: p.defaultValue !== undefined,
    }));
    const removableCount = inlineParams.filter((p) => p.defaultValue === undefined).length;
    return { inlineParams, inlineKeys, options, removableCount };
  }, [advancedParams, isParamEmpty, manuallyShownAdvanced]);

  const handleAdvancedSelectionChange = useCallback(
    (next: string[]) => {
      const nextSet = new Set(next);
      // Clear any params the user just removed so they stop serializing.
      for (const key of advancedPicker.inlineKeys) {
        if (!nextSet.has(key)) setParameter(key, null);
      }
      setManuallyShownAdvanced(nextSet);
    },
    [advancedPicker.inlineKeys, setParameter],
  );

  const handleAdvancedShowAll = useCallback(() => {
    setManuallyShownAdvanced(new Set(advancedParams.map((p) => p.name)));
  }, [advancedParams]);

  const handleAdvancedHideAll = useCallback(() => {
    for (const p of advancedParams) {
      if (p.defaultValue === undefined) setParameter(p.name, null);
    }
    setManuallyShownAdvanced(new Set());
  }, [advancedParams, setParameter]);

  const handleAskOttoParams = useCallback(() => {
    if (!node || isAiBusy) return;
    const nodeName = node.innerActionName ?? node.name;
    const paramNames = requiredParams.map(p => p.displayName ?? p.name).join(', ');
    const actionType = node.nodeData?.type || '';

    if (actionType === 'JavaScriptCode') {
      queuePrompt(
        'Help me write JavaScript code for the "' + nodeName + '" action. The code runs in a sandbox with workflowContext. It must use return to produce output. Available globals: JSON, Math, Date, Array, Object, String, console. No require/import.',
        { type: 'single', nodeNames: [node.name] },
        'empty-params',
      );
    } else {
      const prompt = paramNames
        ? `Help me configure the "${nodeName}" action. It needs these parameters: ${paramNames}`
        : `Help me configure the "${nodeName}" action`;
      queuePrompt(prompt, { type: 'single', nodeNames: [node.name] }, 'empty-params');
    }
  }, [node, isAiBusy, requiredParams, queuePrompt]);

  const handleChange = useCallback((name: string, value: string | number | boolean | object | null) => {
    setParameter(name, value);
  }, [setParameter]);

  // Get code interpreter state for Agent nodes
  const codeInterpreterEnabled = useMemo(() => parameterValues['_codeInterpreterEnabled'] as boolean | undefined, [parameterValues]);
  
  const handleCodeInterpreterToggle = useCallback((checked: boolean) => {
    setParameter('_codeInterpreterEnabled', checked);
  }, [setParameter]);

  // Helper to get string value from parameter (handles ValueSegment[] or primitive)
  const getStringValue = useCallback((value: unknown): string => {
    if (isValueSegmentArray(value)) {
      return segmentsToString(value);
    }
    return (value as string) || '';
  }, []);


  // Helper to get value for a parameter, supporting nested paths like "body.To"
  const getParameterValue = useCallback((name: string): unknown => {
    // First check if we have a direct value in parameterValues
    if (name in parameterValues) {
      return parameterValues[name];
    }

    // Swagger headers are stored under `header.<name>` to avoid colliding
    // with query/body keys; fall back so the editor field renders the value.
    const headerKey = `header.${name}`;
    if (headerKey in parameterValues) {
      return parameterValues[headerKey];
    }

    // Body-root fields carry the entire request body; extraction stores
    // primitive bodies under the literal `body` key.
    const def = mergedParameterDefs.find((p) => p.name === name);
    if (def?._parameterInfo?.info?.isBodyRoot && 'body' in parameterValues) {
      return parameterValues['body'];
    }

    // Check simpleParameterValues for swagger-extracted path parameters (e.g., groupId, channelId)
    // These are extracted from the path using swagger path template and stored in simpleParameterValues
    // but not in parameterValues (since they're derived during render, not during initial extraction)
    if (name in simpleParameterValues) {
      return simpleParameterValues[name];
    }

    // For dynamic schema params like "recipient.groupId", the value might be stored
    // as "body.recipient" (object) in parameterValues. Try extracting the nested property.
    if (name.includes('.')) {
      const parts = name.split('.');
      const parentKey = parts[0]; // e.g., "recipient"
      const childPath = parts.slice(1); // e.g., ["groupId"]

      // Try "body.{parentKey}" in parameterValues (e.g., "body.recipient")
      const bodyKey = `body.${parentKey}`;
      if (bodyKey in parameterValues) {
        let current: unknown = parameterValues[bodyKey];
        for (const part of childPath) {
          if (current && typeof current === 'object' && part in (current as Record<string, unknown>)) {
            current = (current as Record<string, unknown>)[part];
          } else {
            current = undefined;
            break;
          }
        }
        if (current !== undefined) {
          return current;
        }
      }

      // Try extracting from nodeInputs with original path
      if (nodeInputs) {
        let current: unknown = nodeInputs;
        for (const part of parts) {
          if (current && typeof current === 'object' && part in (current as Record<string, unknown>)) {
            current = (current as Record<string, unknown>)[part];
          } else {
            current = undefined;
            break;
          }
        }
        if (current !== undefined) {
          return current;
        }

        // Try with "body." prefix in nodeInputs (e.g., "body.recipient.groupId")
        current = nodeInputs;
        const bodyParts = ['body', ...parts];
        for (const part of bodyParts) {
          if (current && typeof current === 'object' && part in (current as Record<string, unknown>)) {
            current = (current as Record<string, unknown>)[part];
          } else {
            current = undefined;
            break;
          }
        }
        if (current !== undefined) {
          return current;
        }
      }

      return undefined;
    }

    // For non-nested paths, also try with "body." prefix
    const bodyKey = `body.${name}`;
    if (bodyKey in parameterValues) {
      return parameterValues[bodyKey];
    }

    return undefined;
  }, [parameterValues, simpleParameterValues, nodeInputs, mergedParameterDefs]);

  /** Use the parameter's authored placeholder when present; otherwise leave the editor empty. */
  const getEffectivePlaceholder = (param: ParameterDef): string | undefined => {
    return param.placeholder;
  };

  // Field-action chip handlers. Snapshot the chip's bounding rect into the
  // store so the FieldFork popover can position itself.
  const handleTokenChipClick = useCallback(
    (parameterId: string, anchor: DOMRect) => {
      openFieldFork(parameterId, 'tokens', anchor, 'chip');
    },
    [openFieldFork],
  );

  const handleExpressionChipClick = useCallback(
    (parameterId: string, anchor: DOMRect) => {
      openFieldFork(parameterId, 'expr', anchor, 'chip');
    },
    [openFieldFork],
  );

  const handleDeploymentFieldChange = useCallback(
    (parameterId: string, deployment: DeploymentOption) => {
      setParameter(parameterId, deployment.name);

      if (deployment.id) {
        const dependentParameters = parameters.filter(
          p => p._parentGroup?.key?.toLowerCase() === 'agentmodelsettings.deploymentmodelproperties'
        );
        for (const param of dependentParameters) {
          if (param._parameterInfo?.parameterName === 'name' || param.name.endsWith('.name')) {
            setParameter(param.name, deployment.modelName);
          }

          if (param._parameterInfo?.parameterName === 'version' || param.name.endsWith('.version')) {
            setParameter(param.name, deployment.modelVersion);
          }

          if (param._parameterInfo?.parameterName === 'format' || param.name.endsWith('.format')) {
            setParameter(param.name, deployment.modelFormat);
          }
        }
      }
    },
    [parameters, setParameter],
  );

  const renderField = useCallback((rawParam: ParameterDef, endActions?: ReactNode) => {
    // Apply the chip-prompt placeholder for tokenizable fields without an
    // authored placeholder, once at the top so every field-type branch
    // below sees the override consistently.
    const effectivePlaceholder = getEffectivePlaceholder(rawParam);
    const param: ParameterDef =
      effectivePlaceholder !== undefined && effectivePlaceholder !== rawParam.placeholder
        ? { ...rawParam, placeholder: effectivePlaceholder }
        : rawParam;
    const value = getParameterValue(param.name);
    const error = validationErrors[param.name];

    // Special handling for body parameter with object value
    // When body is stored as an object (common in swagger-based operations),
    // render each property as a separate field for better UX
    if (param.name === 'body' && typeof value === 'object' && value !== null && !Array.isArray(value)) {
      const bodyObj = value as Record<string, unknown>;
      const keys = Object.keys(bodyObj);

      // Only render as separate fields if the body has properties
      if (keys.length > 0) {
        return (
          <div key={param.name} className={styles.bodyFieldGroup}>
            {keys.map((key) => {
              const propValue = bodyObj[key];
              const fieldName = `body.${key}`;
              const displayName = key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1').trim();

              // Render field based on value type
              if (typeof propValue === 'boolean') {
                return (
                  <DropdownField
                    key={fieldName}
                    name={fieldName}
                    label={displayName}
                    value={propValue ? 'true' : 'false'}
                    onChange={(v) => {
                      const newBody = { ...bodyObj, [key]: v === 'true' };
                      handleChange('body', newBody);
                    }}
                    options={[
                      { label: 'True', value: 'true' },
                      { label: 'False', value: 'false' },
                    ]}
                  />
                );
              }

              // Known enum fields - render as dropdown
              if (key === 'contentType' || key === 'format' || key === 'type') {
                // Default options for common enum-like fields
                const enumOptions = key === 'contentType'
                  ? [{ label: 'Text', value: 'text' }, { label: 'HTML', value: 'html' }]
                  : [{ label: String(propValue), value: String(propValue) }];

                return (
                  <DropdownField
                    key={fieldName}
                    name={fieldName}
                    label={displayName}
                    value={String(propValue ?? '')}
                    onChange={(v) => {
                      const newBody = { ...bodyObj, [key]: v };
                      handleChange('body', newBody);
                    }}
                    options={enumOptions}
                  />
                );
              }

              // Text content fields - use textarea for longer content
              if (key === 'content' || key === 'message' || key === 'body' || key === 'text') {
                return (
                  <TextAreaField
                    key={fieldName}
                    name={fieldName}
                    label={displayName}
                    value={String(propValue ?? '')}
                    onChange={(v) => {
                      const newBody = { ...bodyObj, [key]: v };
                      handleChange('body', newBody);
                    }}
                    required={param.required}
                    error={error}
                    showAgentParameterButton={showAgentParameterButton}
                    agentParameterSchema={agentParameterSchema}
                    actionOutputs={combinedActionOutputs}
                    hasRunData={hasRunData}
                    isStale={isStale}
                  />
                );
              }

              // Default: string field
              return (
                <StringField
                  key={fieldName}
                  name={fieldName}
                  label={displayName}
                  value={String(propValue ?? '')}
                  onChange={(v) => {
                    const newBody = { ...bodyObj, [key]: v };
                    handleChange('body', newBody);
                  }}
                  required={param.required}
                  error={error}
                  showAgentParameterButton={showAgentParameterButton}
                  agentParameterSchema={agentParameterSchema}
                  actionOutputs={combinedActionOutputs}
                  hasRunData={hasRunData}
                  isStale={isStale}
                />
              );
            })}
          </div>
        );
      }
    }

    // Get value appropriate for field type
    // String/text fields accept ValueSegment[] or string directly
    // Falls back to defaultValue from manifest when no value is set
    const getFieldValue = (): string | ValueSegment[] => {
      if (isValueSegmentArray(value)) {
        return value;
      }
      if (value !== undefined && value !== null) {
        return value as string;
      }
      // Use default value from manifest when no value is set (e.g., new action)
      if (param.defaultValue !== undefined) {
        return String(param.defaultValue);
      }
      return '';
    };

    // Convert object/array values to JSON string for text-based editors
    const getJsonValue = (): string | ValueSegment[] => {
      if (isValueSegmentArray(value)) {
        return value;
      }
      if (typeof value === 'object' && value !== null) {
        return JSON.stringify(value, null, 2);
      }
      return (value as string) || '';
    };

    switch (param.type) {
      case 'string':
        return (
          <StringField
            key={param.name}
            name={param.name}
            label={param.displayName}
            value={getFieldValue()}
            onChange={(v) => handleChange(param.name, v)}
            description={param.description}
            required={param.required}
            error={error}
            placeholder={param.placeholder}
            showAgentParameterButton={showAgentParameterButton}
            agentParameterSchema={agentParameterSchema}
            actionOutputs={combinedActionOutputs}
            hasRunData={hasRunData}
            isStale={isStale}
            endActions={endActions}
          />
        );

      case 'text':
        return (
          <TextAreaField
            key={param.name}
            name={param.name}
            label={param.displayName}
            value={getFieldValue()}
            onChange={(v) => handleChange(param.name, v)}
            description={param.description}
            required={param.required}
            error={error}
            placeholder={param.placeholder}
            showAgentParameterButton={showAgentParameterButton}
            agentParameterSchema={agentParameterSchema}
            actionOutputs={combinedActionOutputs}
            hasRunData={hasRunData}
            isStale={isStale}
            endActions={endActions}
          />
        );

      case 'code': {
        // Determine language from editor options if available
        const codeEditorOpts = param.editorOptions as { language?: string } | undefined;
        const language = codeEditorOpts?.language as CodeLanguage | undefined ?? 'javascript';
        return (
          <CodeField
            key={param.name}
            name={param.name}
            label={param.displayName}
            value={getFieldValue()}
            onChange={(v) => handleChange(param.name, v)}
            description={param.description}
            required={param.required}
            error={error}
            language={language}
            workflowActions={language === 'javascript' ? workflowActions : undefined}
          />
        );
      }

      case 'number': {
        const numericSchema = param._parameterInfo?.schema as
          | { type?: string; minimum?: number; maximum?: number }
          | undefined;
        const isInteger = numericSchema?.type === 'integer';
        return (
          <NumberField
            key={param.name}
            name={param.name}
            label={param.displayName}
            value={value as number | string | ValueSegment[] | undefined}
            onChange={(v) => handleChange(param.name, v ?? null)}
            description={param.description}
            required={param.required}
            error={error}
            placeholder={param.placeholder}
            isInteger={isInteger}
            min={numericSchema?.minimum}
            max={numericSchema?.maximum}
            endActions={endActions}
            showAgentParameterButton={showAgentParameterButton}
            agentParameterSchema={agentParameterSchema}
            actionOutputs={combinedActionOutputs}
            hasRunData={hasRunData}
            isStale={isStale}
          />
        );
      }

      case 'boolean': {
        const useDropdown = param.editorOptions?.['useDropdown'] === true;
        return (
          <BooleanField
            key={param.name}
            name={param.name}
            label={param.displayName}
            value={(value as boolean) || false}
            onChange={(v) => handleChange(param.name, v)}
            description={param.description}
            required={param.required}
            error={error}
            mode={useDropdown ? 'dropdown' : 'switch'}
            endActions={endActions}
          />
        );
      }

      case 'dropdown':
        // Static dropdown only - dynamic values now use combobox type
        return (
          <DropdownField
            key={param.name}
            name={param.name}
            label={param.displayName}
            value={getStringValue(value)}
            options={param.options || []}
            onChange={(v) => handleChange(param.name, v)}
            description={param.description}
            required={param.required}
            error={error}
            placeholder={param.placeholder}
          />
        );

      case 'combobox':
        // Check if this combobox has dynamic values configuration
        if (hasDynamicValuesConfig(param._parameterInfo)) {
          return (
            <DynamicComboboxField
              key={param.name}
              name={param.name}
              label={param.displayName}
              value={getFieldValue()}
              onChange={(v) => handleChange(param.name, v)}
              parameterInfo={param._parameterInfo!}
              staticOptions={param.options || []}
              description={param.description}
              placeholder={param.placeholder}
              required={param.required}
              error={error}
              showAgentParameterButton={showAgentParameterButton}
              agentParameterSchema={agentParameterSchema}
              actionOutputs={combinedActionOutputs}
              hasRunData={hasRunData}
              isStale={isStale}
            />
          );
        }
        // Static combobox (no dynamic values)
        return (
          <ComboboxField
            key={param.name}
            name={param.name}
            label={param.displayName}
            value={getFieldValue()}
            options={param.options || []}
            onChange={(v) => handleChange(param.name, v)}
            description={param.description}
            required={param.required}
            error={error}
            placeholder={param.placeholder}
            showAgentParameterButton={showAgentParameterButton}
            agentParameterSchema={agentParameterSchema}
            actionOutputs={combinedActionOutputs}
            hasRunData={hasRunData}
            isStale={isStale}
          />
        );

      case 'dictionary':
        return (
          <DictionaryField
            key={param.name}
            name={param.name}
            label={param.displayName}
            value={getJsonValue()}
            onChange={(v) => handleChange(param.name, v)}
            description={param.description}
            required={param.required}
            error={error}
            actionOutputs={combinedActionOutputs}
            hasRunData={hasRunData}
            isStale={isStale}
            endActions={endActions}
          />
        );

      case 'array': {
        const rawDefaultView = param.editorOptions?.['defaultView'];
        const defaultView: 'list' | 'json' | undefined =
          rawDefaultView === 'json' || rawDefaultView === 'list' ? rawDefaultView : undefined;
        return (
          <ArrayField
            key={param.name}
            name={param.name}
            label={param.displayName}
            value={getJsonValue()}
            onChange={(v) => handleChange(param.name, v)}
            description={param.description}
            required={param.required}
            error={error}
            placeholder={param.placeholder}
            itemSchema={param.itemSchema}
            definitions={manifest?.properties?.inputs?.definitions as Record<string, unknown> | undefined}
            actionOutputs={combinedActionOutputs}
            hasRunData={hasRunData}
            isStale={isStale}
            defaultView={defaultView}
            endActions={endActions}
          />
        );
      }

      case 'schema':
        return (
          <SchemaField
            key={param.name}
            name={param.name}
            label={param.displayName}
            value={getJsonValue()}
            onChange={(v) => handleChange(param.name, v)}
            description={param.description}
            required={param.required}
            error={error}
            endActions={endActions}
          />
        );

      case 'condition': {
        const isOldFormat = !!(param.editorOptions as Record<string, unknown> | undefined)?.isOldFormat;
        return (
          <ConditionField
            key={param.name}
            name={param.name}
            label={param.displayName}
            value={getJsonValue()}
            onChange={(v) => handleChange(param.name, v)}
            description={param.description}
            required={param.required}
            error={error}
            isOldFormat={isOldFormat}
            showAgentParameterButton={showAgentParameterButton}
            agentParameterSchema={agentParameterSchema}
            actionOutputs={combinedActionOutputs}
            hasRunData={hasRunData}
            isStale={isStale}
            endActions={endActions}
          />
        );
      }

      case 'authentication':
        return (
          <AuthenticationField
            key={param.name}
            name={param.name}
            label={param.displayName}
            value={getJsonValue()}
            onChange={(v) => handleChange(param.name, v)}
            description={param.description}
            required={param.required}
            error={error}
            showAgentParameterButton={showAgentParameterButton}
            agentParameterSchema={agentParameterSchema}
            actionOutputs={combinedActionOutputs}
            hasRunData={hasRunData}
            isStale={isStale}
            endActions={endActions}
          />
        );

      case 'table':
        return (
          <TableField
            key={param.name}
            name={param.name}
            label={param.displayName}
            value={getJsonValue()}
            onChange={(v) => handleChange(param.name, v)}
            description={param.description}
            required={param.required}
            error={error}
            showAgentParameterButton={showAgentParameterButton}
            agentParameterSchema={agentParameterSchema}
            actionOutputs={combinedActionOutputs}
            hasRunData={hasRunData}
            isStale={isStale}
            endActions={endActions}
          />
        );

      case 'copyable':
        // Read-only copyable field — value is populated externally
        // (e.g. trigger callback URL fetched from the runtime data-plane).
        // For `callbackUrl` we render the fetched URL directly rather than
        // mirroring it into parameterValues, to avoid marking the editor
        // dirty on every panel open.
        {
          const fieldValue = getFieldValue();
          const stringFromValue = isValueSegmentArray(fieldValue)
            ? segmentsToString(fieldValue)
            : fieldValue;
          const stringValue =
            param.name === 'callbackUrl' && isRequestTrigger
              ? (triggerCallbackUrl ?? '')
              : stringFromValue;
          return (
            <CopyableField
              key={param.name}
              name={param.name}
              label={param.displayName}
              value={stringValue}
              description={param.description}
              required={param.required}
              error={error}
              placeholder={param.placeholder}
              loading={
                param.name === 'callbackUrl' && isFetchingCallback && !stringValue
              }
            />
          );
        }

      case 'deployment':
        return (
          <DeploymentField
            key={param.name}
            connectionReferenceName={connectionReferenceName}
            value={getStringValue(value)}
            onChange={(v) => handleDeploymentFieldChange(param.name, v)}
            label={param.displayName}
            required={param.required ?? false}
            isCodingAgent={isCodingAgent}
            {...(param.description ? { description: param.description } : {})}
            {...(error ? { error } : {})}
          />
        );

      case 'workflowpicker':
        return (
          <WorkflowPickerField
            key={param.name}
            name={param.name}
            label={param.displayName}
            value={getStringValue(value)}
            onChange={(v) => handleChange(param.name, v)}
            currentWorkflowName={currentWorkflowName}
            description={param.description}
            placeholder={param.placeholder}
            required={param.required}
            error={error}
          />
        );

      case 'readonlyexternal': {
        const editorOpts = (param.editorOptions ?? {}) as {
          externalLink?: { builderId?: string; label?: string };
          provenanceLabel?: string;
          emptyText?: string;
        };
        const externalLink = editorOpts.externalLink?.builderId
          ? {
              builderId: editorOpts.externalLink.builderId,
              ...(editorOpts.externalLink.label
                ? { label: editorOpts.externalLink.label }
                : {}),
            }
          : undefined;
        const fieldValue = getFieldValue();
        const stringValue = isValueSegmentArray(fieldValue)
          ? segmentsToString(fieldValue)
          : (fieldValue as string | undefined);
        return (
          <ReadOnlyExternalField
            key={param.name}
            name={param.name}
            label={param.displayName}
            value={stringValue}
            description={param.description}
            required={param.required}
            provenanceLabel={editorOpts.provenanceLabel}
            emptyText={editorOpts.emptyText}
            externalLink={externalLink}
            parameterValues={parameterValues}
            linkContext={{
              ...(dynamicSchemaConnectionReference
                ? { connectionReference: dynamicSchemaConnectionReference }
                : {}),
            }}
          />
        );
      }

      case 'agentinstruction':
        return (
          <AgentInstructionField
            key={param.name}
            name={param.name}
            label={param.displayName}
            value={getJsonValue()}
            onChange={(v) => handleChange(param.name, v)}
            description={param.description}
            required={param.required}
            error={error}
            placeholder={param.placeholder}
            showAgentParameterButton={showAgentParameterButton}
            agentParameterSchema={agentParameterSchema}
            actionOutputs={combinedActionOutputs}
            hasRunData={hasRunData}
            isStale={isStale}
          />
        );

      case 'initializevariable':
        return (
          <InitializeVariableField
            key={param.name}
            name={param.name}
            label={param.displayName}
            value={getJsonValue()}
            onChange={(v) => handleChange(param.name, v)}
            description={param.description}
            required={param.required}
            error={error}
            placeholder={param.placeholder}
            showAgentParameterButton={showAgentParameterButton}
            agentParameterSchema={agentParameterSchema}
            actionOutputs={combinedActionOutputs}
            hasRunData={hasRunData}
            isStale={isStale}
          />
        );

      case 'setvariable': {
        const rawName = getParameterValue('name');
        const nameValue: string | ValueSegment[] = isValueSegmentArray(rawName)
          ? rawName
          : getStringValue(rawName);
        const rawValue = getParameterValue('value');
        const valueValue =
          typeof rawValue === 'object' && rawValue !== null && !isValueSegmentArray(rawValue)
            ? JSON.stringify(rawValue, null, 2)
            : getStringValue(rawValue);
        return (
          <SetVariableField
            key={param.name}
            nameValue={nameValue}
            valueValue={valueValue}
            onNameChange={(v) => handleChange('name', v)}
            onValueChange={(v) => handleChange('value', v)}
            description={param.description}
            required={param.required}
            disabled={isReadOnly}
            error={error}
            showAgentParameterButton={showAgentParameterButton}
            agentParameterSchema={agentParameterSchema}
            actionOutputs={combinedActionOutputs}
            hasRunData={hasRunData}
            isStale={isStale}
          />
        );
      }

      case 'variablename':
        return (
          <VariableNameField
            key={param.name}
            name={param.name}
            label={param.displayName}
            value={getFieldValue()}
            onChange={(v) => handleChange(param.name, v)}
            description={param.description}
            required={param.required}
            error={error}
            placeholder={param.placeholder ?? 'Select a variable'}
            editorOptions={param.editorOptions}
            hasRunData={hasRunData}
            isStale={isStale}
          />
        );

      case 'recurrence': {
        const recurrenceData = parameterValues['_recurrence'] as RecurrenceValue | undefined;

        return (
          <RecurrenceField
            key={param.name}
            name={param.name}
            value={recurrenceData}
            onChange={(newValue) => handleChange('_recurrence', newValue)}
          />
        );
      }

      case 'html': {
        const htmlMode = (param.editorOptions?.['richTextMode'] as string | undefined) === 'raw-html'
          ? 'raw'
          : 'rich';
        return (
          <HtmlField
            key={param.name}
            name={param.name}
            label={param.displayName}
            value={getStringValue(value)}
            onChange={(html) => handleChange(param.name, html)}
            placeholder={param.placeholder}
            required={param.required}
            initialMode={htmlMode}
            actionOutputs={combinedActionOutputs}
            hasRunData={hasRunData}
            isStale={isStale}
            endActions={endActions}
          />
        );
      }

      case 'filepicker': {
        const dynamicTree = param._parameterInfo?.info?.dynamicTree;
        const stringValue = getStringValue(value);
        return (
          <FilePickerField
            key={param.name}
            name={param.name}
            label={param.displayName}
            value={stringValue}
            displayValue={pickerDisplayValues[stringValue]}
            onChange={(nextValue, display) => {
              // Update the parameter value, then update the picker map.
              // Passing `stringValue` as previousValue purges the prior
              // entry so a clear or a replace doesn't strand stale
              // `{ [oldId]: oldPath }` keys in `action.metadata`.
              handleChange(param.name, nextValue);
              setPickerDisplayValue(nextValue, display, stringValue);
            }}
            dynamicTree={dynamicTree}
            placeholder={param.placeholder}
            endActions={endActions}
          />
        );
      }

      default:
        return (
          <StringField
            key={param.name}
            name={param.name}
            label={param.displayName}
            value={getFieldValue()}
            onChange={(v) => handleChange(param.name, v)}
            description={param.description}
            required={param.required}
            error={error}
            placeholder={param.placeholder}
            showAgentParameterButton={showAgentParameterButton}
            agentParameterSchema={agentParameterSchema}
            actionOutputs={combinedActionOutputs}
            hasRunData={hasRunData}
            isStale={isStale}
          />
        );
    }
  }, [getParameterValue, validationErrors, styles.bodyFieldGroup, showAgentParameterButton, agentParameterSchema, combinedActionOutputs, hasRunData, isStale, handleChange, getStringValue, manifest?.properties?.inputs?.definitions, connectionReferenceName, isCodingAgent, currentWorkflowName, workflowActions, isRequestTrigger, triggerCallbackUrl, isFetchingCallback, handleDeploymentFieldChange, parameterValues, dynamicSchemaConnectionReference, isReadOnly, pickerDisplayValues, setPickerDisplayValue]);

  /**
   * Wrap a rendered field with the FieldActionChips affordance when the
   * parameter is tokenizable. Body-group renders (param.name === 'body'
   * with object children) skip the wrap because those expand into a list
   * of inner fields that will get their own chips if applicable.
   *
   * The chips are passed into the field component as an `endActions`
   * slot. Each field renders that slot in its own labelRow on the
   * right edge — matching the expanded ParameterField layout — so the
   * chips are never inside the bordered input box.
   */
  const renderFieldWithChips = useCallback((param: ParameterDef): ReactNode => {
    const value = getParameterValue(param.name);
    const isBodyGroup =
      param.name === 'body' &&
      typeof value === 'object' &&
      value !== null &&
      !Array.isArray(value) &&
      Object.keys(value as object).length > 0;
    const chipMode = getCompactParamChipMode(param);
    if (isBodyGroup || chipMode === null) {
      return renderField(param);
    }
    const parameterId = getCompactParamForkId(param);
    const chips = (
      <FieldActionChips
        parameterId={parameterId}
        disabled={isReadOnly}
        onTokenClick={(rect) => handleTokenChipClick(parameterId, rect)}
        onExpressionClick={(rect) => handleExpressionChipClick(parameterId, rect)}
        showAgentParameterButton={showAgentParameterButton}
        fieldName={param.name}
        {...(param.description ? { fieldDescription: param.description } : {})}
        {...(param._parameterInfo?.type ? { fieldType: param._parameterInfo.type } : {})}
        onAgentParameterSelect={(token) => {
          // Structured / non-segment editors (chipMode === 'browse') can't
          // accept a tail-appended token without corrupting their value, so
          // copy the agent-parameter reference to the clipboard instead —
          // mirroring the token/expression chips' browse behavior.
          if (chipMode === 'browse') {
            void copyWithToast(token.value ?? '', token.title);
            return;
          }
          setParameter(param.name, token.value ?? '');
        }}
      />
    );
    // Two rendering paths:
    //   1. Native slot (`endActions` prop) — field renders chips inside
    //      its own labelRow. See {@link NATIVE_END_ACTIONS_TYPES}.
    //   2. External slot (`externalChipSlot`) — chips render absolutely
    //      at the row's top-right for fields without an endActions prop.
    const fieldElement = NATIVE_END_ACTIONS_TYPES.has(param.type)
      ? renderField(param, chips)
      : (
        <>
          <div className={styles.externalChipSlot}>{chips}</div>
          {renderField(param)}
        </>
      );
    return (
      <div
        key={param.name}
        className={styles.fieldRow}
        data-otto-field-row=""
      >
        {fieldElement}
      </div>
    );
  }, [getParameterValue, renderField, handleTokenChipClick, handleExpressionChipClick, showAgentParameterButton, isReadOnly, setParameter, copyWithToast, styles.fieldRow, styles.externalChipSlot]);

  /**
   * Render a list of parameters, wrapping consecutive children of the same
   * flattened nested-object parent in a collapsible group container. Single
   * children fall through to a normal field render so we don't add visual
   * weight when there's nothing to group.
   */
  const renderFieldsWithGroups = useCallback((params: ParameterDef[]) => {
    const out: ReactNode[] = [];
    let i = 0;
    while (i < params.length) {
      const param = params[i]!;
      const groupKey = param._parentGroup?.key;
      if (!groupKey) {
        out.push(renderFieldWithChips(param));
        i++;
        continue;
      }

      // Collect adjacent params that share the same parent group key.
      let j = i + 1;
      while (j < params.length && params[j]!._parentGroup?.key === groupKey) {
        j++;
      }
      const groupParams = params.slice(i, j);

      if (groupParams.length === 1) {
        // Don't add an accordion just for one child — render flat.
        out.push(renderFieldWithChips(param));
      } else {
        const groupLabel = param._parentGroup!.label;
        out.push(
          <Accordion
            key={`group-${groupKey}`}
            collapsible
            defaultOpenItems={[groupKey]}
            className={styles.nestedGroupAccordion}
          >
            <AccordionItem value={groupKey}>
              <AccordionHeader className={styles.nestedGroupHeader}>
                {groupLabel}
              </AccordionHeader>
              <AccordionPanel>
                <div className={styles.nestedGroupBody}>
                  {groupParams.map(renderFieldWithChips)}
                </div>
              </AccordionPanel>
            </AccordionItem>
          </Accordion>
        );
      }
      i = j;
    }
    return out;
  }, [renderFieldWithChips, styles.nestedGroupAccordion, styles.nestedGroupBody, styles.nestedGroupHeader]);

  // Show loading state while fetching manifest parameters
  // - manifestLoading: initial query loading
  // - manifestFetching: any fetch in progress (including refetch)
  // - isWaitingForManifest: we have operationInfo but query hasn't started yet
  // Note: For Agent nodes, we still show the built-in tools section even while loading
  const isLoadingParameters = useMemo(() => manifestLoading || manifestFetching || isWaitingForManifest, [manifestLoading, manifestFetching, isWaitingForManifest]);

  if (isLoadingParameters && !isAgentNode) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>
          <Spinner size="tiny" />
          <Text size={200}>Loading parameters...</Text>
        </div>
      </div>
    );
  }

  // Show manifest error if loading failed
  if (manifestError && !isAgentNode) {
    const errorMessage = manifestError instanceof Error ? manifestError.message : String(manifestError);
    return (
      <div className={styles.container}>
        <div className={styles.manifestError}>
          <Text className={styles.manifestErrorTitle}>Failed to load operation manifest</Text>
          <Text className={styles.manifestErrorMessage}>{errorMessage}</Text>
        </div>
        {isScopedEditEnabled && (
          <button
            className={styles.askOttoBanner}
            onClick={handleAskOttoParams}
            disabled={isAiBusy}
            type="button"
            aria-label="Get help with this action"
          >
            <SparkleRegular className={styles.askOttoBannerIcon} />
            <Text className={styles.askOttoBannerText}>
              Get help with this action
            </Text>
            <ChevronRightRegular className={styles.askOttoBannerChevron} />
          </button>
        )}
      </div>
    );
  }

  if (parameters.length === 0 && !isAgentNode) {
    return (
      <div className={styles.container}>
        <Text className={styles.noParams}>
          No configurable parameters for this node type.
        </Text>
        {isScopedEditEnabled && (
          <button
            className={styles.askOttoBanner}
            onClick={handleAskOttoParams}
            disabled={isAiBusy}
            type="button"
            aria-label="Get help with this action"
          >
            <SparkleRegular className={styles.askOttoBannerIcon} />
            <Text className={styles.askOttoBannerText}>
              Get help with this action
            </Text>
            <ChevronRightRegular className={styles.askOttoBannerChevron} />
          </button>
        )}
      </div>
    );
  }

  /**
   * Single mount point for the field-anchored fork popover. The popover
   * positions via portal so it escapes the panel's clipping ancestors and
   * overlays the rest of the UI; we mount only one at a time, gated by
   * the slice's parameterId matching a param in the current panel. This
   * avoids spinning up upstream-data hooks (`useTokenSources`,
   * `useUpstreamManifests`) per row.
   */
  const renderFieldFork = (): ReactNode => {
    if (!fieldFork.open || !fieldFork.anchor || !fieldFork.parameterId) {
      return null;
    }
    const activeParam = parameters.find(
      (p) => getCompactParamForkId(p) === fieldFork.parameterId,
    );
    if (!activeParam || !activeParam._parameterInfo) {
      return null;
    }
    const rawValue = getParameterValue(activeParam.name);
    let valueSegments: ValueSegment[];
    if (isValueSegmentArray(rawValue)) {
      valueSegments = rawValue;
    } else if (rawValue === undefined || rawValue === null || rawValue === '') {
      valueSegments = [];
    } else {
      const literal = typeof rawValue === 'string' ? rawValue : JSON.stringify(rawValue);
      valueSegments = [
        {
          id: generateSegmentId(),
          type: ValueSegmentType.LITERAL,
          value: literal,
        },
      ];
    }
    return (
      <FieldForkContainer
        parameter={activeParam._parameterInfo}
        value={valueSegments}
        onChange={(next) => handleChange(activeParam.name, next)}
        anchor={fieldFork.anchor}
        initialSource={fieldFork.valueSource}
        commitMode={getCompactParamChipMode(activeParam) ?? 'insert'}
      />
    );
  };

  return (
    <div className={styles.container}>
      {/* AI assist banner — hidden once all required params are filled, gated by scopedEdit flag */}
      {isScopedEditEnabled && (allParamsEmpty || someParamsConfigured) && (
        <button
          className={styles.askOttoBanner}
          onClick={handleAskOttoParams}
          disabled={isAiBusy}
          type="button"
          aria-label={allParamsEmpty
            ? 'Configure with Copilot'
            : 'Finish configuring with Copilot'}
        >
          <SparkleRegular className={styles.askOttoBannerIcon} />
          <Text className={styles.askOttoBannerText}>
            {allParamsEmpty
              ? 'Configure with Copilot'
              : 'Finish configuring with Copilot'}
          </Text>
          <ChevronRightRegular className={styles.askOttoBannerChevron} />
        </button>
      )}

      {/* Required/main parameters */}
      <div className={styles.parameterGroup}>
        {renderFieldsWithGroups(requiredParams)}
      </div>

      {/* Dynamic schema loading indicator */}
      {dynamicSchemaParam && dynamicSchemaLoading && (
        <div className={styles.loading}>
          <Spinner size="tiny" />
          <Text size={200}>Loading additional fields...</Text>
        </div>
      )}

      {/* Dynamic schema error indicator */}
      {dynamicSchemaParam && dynamicSchemaError && !dynamicSchemaLoading && (
        <Text size={200} className={styles.dynamicSchemaError}>
          {dynamicSchemaError}
        </Text>
      )}

      {/* Dynamic schema waiting for a connection (placeholder reference). */}
      {dynamicSchemaParam && dynamicSchemaMissingConnection && !dynamicSchemaLoading && (
        <Text size={200} className={styles.dynamicSchemaWaiting}>
          Configure a connection to load additional fields.
        </Text>
      )}

      {/* Dynamic schema waiting for dependencies */}
      {dynamicSchemaParam && !dynamicSchemaMissingConnection && !dynamicSchemaDependenciesSatisfied && !dynamicSchemaLoading && (
        <Text size={200} className={styles.dynamicSchemaWaiting}>
          Select required fields to load additional options...
        </Text>
      )}

      {/* Built-in tools section for Agent nodes */}
      {isAgentNode && !isFoundryAgentNode && (
        <div className={styles.builtInToolsSection}>
          <Text className={styles.builtInToolsHeader}>Built-in Tools</Text>
          <div className={styles.builtInToolsToggle}>
            <div className={styles.builtInToolsLabel}>
              <Text>Code Interpreter</Text>
              <Text className={styles.builtInToolsDescription}>
                Enable the agent to write and execute JavaScript code for calculations, data analysis, and file processing.
              </Text>
            </div>
            <Switch
              checked={codeInterpreterEnabled ?? false}
              onChange={(_, data) => handleCodeInterpreterToggle(data.checked)}
            />
          </div>
        </div>
      )}

      {/* Tools list + Add tool affordance — applies to all agents (loop + Foundry). */}
      {isAgentNode && node?.name && (
        <AgentToolsSection agentName={node.name} readOnly={isReadOnly} />
      )}

      {/* Picker that toggles which advanced parameters render inline below.
          Mirrors Logic Apps' SearchableDropdownWithAddAll. */}
      {advancedParams.length > 0 && (
        <SearchableDropdownWithAddAll
          label="Advanced parameters"
          placeholder="Add a parameter"
          options={advancedPicker.options}
          selectedKeys={advancedPicker.inlineKeys}
          onSelectedKeysChange={handleAdvancedSelectionChange}
          disabled={isReadOnly}
          addAllButtonText="Show all"
          addAllButtonTooltip="Show every advanced parameter"
          addAllButtonEnabled={advancedPicker.inlineKeys.length < advancedParams.length}
          onShowAllClick={handleAdvancedShowAll}
          removeAllButtonText="Hide all"
          removeAllButtonTooltip="Clear and hide all optional advanced parameters"
          removeAllButtonEnabled={advancedPicker.removableCount > 0}
          onHideAllClick={handleAdvancedHideAll}
        />
      )}

      {advancedPicker.inlineParams.length > 0 && (
        <div className={styles.parameterGroup}>
          {renderFieldsWithGroups(advancedPicker.inlineParams)}
        </div>
      )}

      {/*
        Field-anchored fork popover. Single-mount: only renders for the
        parameter whose chip was clicked, and FieldForkContainer's
        upstream-data hooks only spin up while it's open.
      */}
      {renderFieldFork()}
    </div>
  );
};

ParameterList.displayName = 'ParameterList';